import React from 'react';
import { Button, ControlLink, LoadingBalls, Overlay, Stack, Text } from '@moda/om';
import { AuthAction } from '../../lib/authenticate';
import { SocialAuthButtons } from './SocialAuthButtons';

type SignInFrameProps = {
  title: string;
  message: string;
  className?: string;
  error?: React.ReactNode;
  loading: boolean;
  submitLabel: string;
  showSocialAuth: boolean;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onAuth: (authUser?: import('../../lib/authenticate').AuthPayloadSuccess) => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  showCancelButton?: boolean;
  onCancel?: () => void;
};

export const SignInFrame: React.FC<SignInFrameProps> = ({
  title,
  message,
  className,
  error,
  loading,
  submitLabel,
  showSocialAuth,
  showAuthModal,
  hideAuthModal,
  onSubmit,
  onAuth,
  children,
  footer,
  showCancelButton,
  onCancel
}) => {
  return (
    <>
      <div className={className} data-testid="signInSection">
        <form onSubmit={onSubmit}>
          {title && <div className="Authentication__title">{title}</div>}
          {message && <div className="Authentication__message">{message}</div>}
          {error && (
            <div className="Authentication__error" role="alert">
              {error}
            </div>
          )}

          <Stack space={5}>
            <div className="Authentication__fields">{children}</div>

            <Stack space={2}>
              <Button
                className="Authentication__button Authentication__button--sign-in"
                type="submit"
                data-testid="signInSubmit"
              >
                {submitLabel}
              </Button>

              {showCancelButton && (
                <Button
                  className="Authentication__button Authentication__button--cancel-sign-in"
                  onClick={onCancel || hideAuthModal}
                  secondary
                  type="button"
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </form>

        {showSocialAuth && (
          <>
            <div className="Authentication__divider" />

            <SocialAuthButtons
              action={AuthAction.SignIn}
              showAmazonMessage
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              onAuth={onAuth}
            />

            <div className="Authentication__privacy-policy">
              <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank">
                Privacy Policy
              </ControlLink>
            </div>
          </>
        )}

        {footer && (
          <>
            <div className="Authentication__divider" />
            {footer}
          </>
        )}
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
